// FRONT-216 — PUBLIC, NON-GATING render context for the @crehana/web MainFooter chrome.
//
// WHY THIS EXISTS
// The legal pages are fully public (no auth). But @crehana/web's `MainFooter` reaches for several
// React contexts that the source crehana-frontend monorepo shell (`pages/_app.tsx`) provides for
// ANONYMOUS visitors WITHOUT any login redirect. We replicate the MINIMAL, NON-GATING version of
// each — context only, never a gate:
//
//   1. Apollo client — needed by the @crehana/web chrome tree. The client only needs to EXIST in
//      context; no request fires for anonymous public pages. The shared client's `errorLink` only
//      logs — it NEVER redirects/logs out on a missing session (verified in @crehana/apollo-client).
//
//   2. `useMe()` from @crehana/auth — `useUserWithRoleAndPermissions` + `MainFooter` read `me` +
//      `loading`. We do NOT use @crehana/auth's real `AuthProvider`: it runs `useQuery(ME_QUERY)`
//      over the network on every page (undesirable for static public pages). Instead we feed the raw
//      `AuthContext` a fixed ANONYMOUS, logged-out value (`me: null`, `loading: false`). The chrome
//      then derives `isAnonymous = true` and every flag falls back to its public default — exactly
//      the logged-out state anonymous visitors see on crehana.com. `refetch`/`updateMe`/`logout` are
//      inert (never invoked for anonymous chrome) and `talentUser` is an empty record.
//
// NOTE: AmplitudeProvider was removed — it was only needed for MainMenu's `useActiveSubmenu`.
//       MainMenu has been replaced by LegalBanner (FRONT-216), so the provider is no longer required.
//
// ABSOLUTE CONSTRAINT: this stack must NEVER redirect to login, NEVER block render, NEVER require a
// session. It only supplies render context so the public chrome renders in its logged-out state.
import { ApolloProvider, NetworkStatus } from '@apollo/client';
import { useApolloClient } from '@crehana/apollo-client';
import { AuthContext, type State, type TalentUser } from '@crehana/auth';
import type { CountryCode } from '@crehana/ts-types';
import type { ReactNode } from 'react';

// Inert talent profile for an anonymous visitor (no talent product, no modules, no roles).
const ANONYMOUS_TALENT_USER: TalentUser = {
  job: '',
  employeeId: '',
  division: '',
  bossFullName: '',
  subsidiary: '',
  hasTalentProduct: false,
  isLeader: false,
  talentModulesCodes: [],
  talentModules: [],
  talentAdminModules: [],
  roles: [],
  loading: false,
  is_boss: false,
};

// Fixed anonymous (logged-out) me-context value — shape matches @crehana/auth's `State`.
// No network: `refetch` resolves a ready, empty `me` result; `updateMe`/`logout` are no-ops.
const ANONYMOUS_AUTH_VALUE: State = {
  data: {
    me: null,
    loading: false,
    error: undefined,
    refetch: () =>
      Promise.resolve({
        data: { me: null },
        loading: false,
        networkStatus: NetworkStatus.ready,
      }),
    updateMe: () => undefined,
    talentUser: ANONYMOUS_TALENT_USER,
  },
  logout: () => undefined,
};

interface PublicChromeContextProviderProps {
  children: ReactNode;
  countryCode: CountryCode;
}

/**
 * Supplies the minimal, NON-GATING context (Apollo client + fixed anonymous me)
 * so the @crehana/web chrome (MainFooter) renders in its public, logged-out state.
 */
const PublicChromeContextProvider = ({
  children,
  countryCode,
}: PublicChromeContextProviderProps) => {
  const apolloClient = useApolloClient(undefined, countryCode);

  return (
    <ApolloProvider client={apolloClient}>
      <AuthContext.Provider value={ANONYMOUS_AUTH_VALUE}>
        {children}
      </AuthContext.Provider>
    </ApolloProvider>
  );
};

export default PublicChromeContextProvider;
